<template>
	<div>
		<div class="row" v-if="!selectedStudent">
			<div class="col-sm-12">
				<parent-home-page :students="$store.state.students"></parent-home-page>
			</div>
		</div>

		<!-- Locale Selection -->
		<template v-if="selectLocale">
			<!-- Modal -->
			<modal :modal-title="$t('MAIN_CONTENT.General.languageSelectionTitle')"
							:show="selectLocale"
							dialog-size="sm"
							name="courseRequestModal"
							@close="selectedLocale.length === 0 ? '' : saveLocale()">
				<template slot-scope="modalProps">
					<p class="text-center text-muted">
						{{ $t('MAIN_CONTENT.General.languageSelectionTitle') }}
					</p>
					<div class="col-xs-12 alert alert-danger" v-if="showLocaleSaveError">
						<font-awesome-icon icon="faExclamationTriangle"></font-awesome-icon>
						{{ $t( 'MAIN_CONTENT.HomePage.localeSaveError' ) }}
					</div>
					<!-- Language Selection -->
					<div class="col-xs-12">
						<strong>
							{{ $t('MAIN_CONTENT.General.selectLanguage') }}
						</strong>
					</div>
					<div class="form-group">
						<select name="localeSelection" id="localeSelect" class="form-control" v-model="selectedLocale">
							<option value="">{{ $t( 'MAIN_CONTENT.General.languageSelectionTitle' ) }}</option>
							<option v-for="( locale, localeIndex ) in locales"
											:value="locale.LOCALE_ID"
											:key="`locale_${localeIndex}`">
								{{ locale.LOCALE_DISPLAY_TEXT }}
							</option>
						</select>
					</div>
				</template>
				<template slot="footer">
					<button type="button"
									class="btn btn-transparent pull-right"
									@click="saveLocale()"
									:disabled="selectedLocale.length <= 0">
						{{ $t('MAIN_CONTENT.commonContent.submitButton') }}
					</button>
				</template>
			</modal>
		</template>

		<template v-if="!!selectedStudent">
			<!-- COVID-19 Link -->
			<div v-if="roleRules.COVID19_SURVEY_LINK === 'Y' && selectedStudent.RESTRICTED_ACCOUNT_FLG === 'N' && !!covidLink">
				<div class="col-sm-12 alert alert-warning">
					{{ $t('MAIN_CONTENT.HomePage.CovidSurveyMessage') }}<a :href="covidLink" target="_blank">COVID-19 Daily Screening</a>.
				</div>
			</div>

			<!-- Announcements -->
			<div class="row" v-if="roleRules.BUILDING_NEWS_FLG === 'Y' && announcements.length">
				<div class="col-sm-12">
					<announcements :title="$t('MAIN_CONTENT.Announcements.labelAnnouncements')"
												:announcements='announcements'
												:show-dismiss="false">
					</announcements>
				</div>
			</div>

			<!-- Upcoming Assignments -->
			<div class="row"
					:key="`assignment_${owlKey}`"
					v-if="assignments.length > 0 && (role === 'student' || !!selectedStudent) && owlKey > 0">
				<div class="col-sm-12 class-page">
					<panel-basic :title="$t( 'MAIN_CONTENT.HomePage.upcomingAssignments' )"
											id="assignments"
											:collapsable="false"
											panelClass="owl-heading"
											overflow="hidden"
											class="class-assignments">
						<span slot="title">
							{{ $t('MAIN_CONTENT.commonContent.classPage_upcomingAssignments') }}
							<small style="text-transform: none;">
								{{ $t('MAIN_CONTENT.commonContent.classPage_upcomingAssignmentTotal', { count: assignments.length }) }}
								<template v-if="dueSoon.length"> | </template> 
								<span class="text-danger"
											v-if="dueSoon.length"
											v-tooltip.top-center="{content: $t('commonText.dueSoonTextDescription'), trigger: 'hover click'}">
									{{ $t('MAIN_CONTENT.AssignmentsCarousel.classPage_dueSoon', { count: dueSoon.length }) }}
								</span>
							</small>
						</span>
						<div class="view-options">
							<button class="btn btn-transparent" 
											:class="{ 'active': assignmentsView === 'slider' }"
											type="button"
											@click="assignmentsView = 'slider'">
								<i class="fas fa-columns" aria-hidden="true"></i>
								<span class="sr-only">{{$t('MAIN_CONTENT.classPage.index.classPage_sliderView')}}</span>
							</button>
							<button class="btn btn-transparent"
											:class="{ 'active': assignmentsView === 'grid' }"
											type="button"
											@click="assignmentsView = 'grid'">
								<i class="fas fa-th" aria-hidden="true"></i>
								<span class="sr-only">{{$t('MAIN_CONTENT.classPage.index.classPage_gridView')}}</span>
							</button>
							<button class="btn btn-transparent"
											:class="{ 'active': assignmentsView === 'list' }"
											type="button"
											@click="assignmentsView = 'list'">
								<i class="fas fa-th-list" aria-hidden="true"></i>
								<span class="sr-only">{{$t('MAIN_CONTENT.classPage.index.classPage_listView')}}</span>
							</button>
						</div>
						<!-- assignments in slider view -->
						<assignments-carousel :carousel-class="`assignments-carousel color-status ${getDisplayStyle}`"
																	:carousel-data="assignments"
																	carousel-id="ac-cwp-01"
																	color-variant="blank"
																	:margin="10"
																	:show-nav="true"
																	:show-course="false"
																	v-if="assignments.length && assignmentsView === 'slider'">
						</assignments-carousel>

						<!-- assignments in grid view -->
						<assignments-grid grid-class="assignments-grid color-status" 
															:grid-data="assignments"
															grid-id="ag-cwp-01" 
															:show-course="false"
															v-else-if="assignments.length && assignmentsView === 'grid'">
						</assignments-grid>

						<!-- assignments in list view -->
						<assignments-list list-class="assignments-list color-status"
															:list-data="assignments"
															list-id="al-cwp-01"
															:show-course="false"
															v-else-if="assignments.length && assignmentsView === 'list'">
						</assignments-list>
					</panel-basic>
				</div>
			</div>

			<div class="row">
				<div class="flex-equal-heights">
					<!-- Next Year Course Request Form -->
					<div class="col-xs-12 col-lg-6 course-request-form-panel"
							v-if="roleRules.COURSE_REQUESTS_FLG === 'Y' && courseRequestForms.length > 0 && (role === 'student' || !!selectedStudent)"
							style="margin-bottom: 5px;">
						<panel-basic id="request-form"
												:collapsable="false"
												panel-class="full-height course-request-height">
							<router-link to="/course-request-forms" class="btn btn-primary btn-lg btn-block text-lg">
								{{ $t('MAIN_CONTENT.HomePage.nextYearCourseRequestFormButton') }}
							</router-link>
						</panel-basic>
					</div>
					<!-- Graduation Progress Bar -->
					<div class="col-xs-12 col-lg-6 graduation-progress-panel"
							v-if="roleRules.GRAD_PROG_FLG === 'Y' && roleRules.GRAD_COURSE_WORK === 'Y' && (role === 'student' || !!selectedStudent)"
							style="margin-bottom: 5px;">
						<panel-basic :title="$t('MAIN_CONTENT.HomePage.graduationProgressLabel')"
												id="grad-progress"
												:collapsable="false"
												panel-class="full-height">
							<div class="col-xs-10">
								<div class="progress">
									<div class="progress-bar completed-progress-bar"
											role="progressbar"
											:aria-valuenow="percentCreditsEarned"
											aria-valuemin="0"
											aria-valuemax="100"
											:style="{ width: `${percentCreditsEarned}` }"
											v-tooltip.top-center="{content: $t('MAIN_CONTENT.HomePage.completed', { percent: percentCreditsEarned } ), trigger: 'hover click'}">
									</div>
									<div class="progress-bar in-progress-background"
											role="progressbar"
											:aria-valuenow="percentCreditsInProgress"
											aria-valuemin="0"
											aria-valuemax="100"
											:style="{width: `${percentCreditsInProgress}`}"
											v-tooltip.top-center="{content: $t('MAIN_CONTENT.HomePage.inProgress', { percent: percentCreditsInProgress } ), trigger: 'hover click'}">
									</div>
								</div>
							</div>
							<div class="col-xs-2">
								<span class="text-lg">
									<strong>
										{{percentCreditsEarned}}
									</strong>
								</span>
							</div>
						</panel-basic>
					</div>
				</div>
			</div>
			<br />
			<br />
			<!-- Interactive Schedules -->
			<panel-plain id="panel-scheduler"
									:title="$t('MAIN_CONTENT.HomePage.interactiveSchedulesLabel')"
									:show-button="false"
									panel-class="dash-student"
									icon-class="hand-pointer"
									:heading-level="2"
									:titleAsLink="true"
									:panelLinkAddress="'/interactive-scheduler/'"
									v-if="interactiveSchedules.length > 0 && (role === 'student' || !!selectedStudent)">
				<table class="table table-striped">
					<thead>
						<tr>
							<th></th>
							<th class="text-right">{{ $t('MAIN_CONTENT.HomePage.windowOpenUntilLabel') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(schedule, index) in interactiveSchedules" :key="`interactive_schedules_${index}`">
							<!-- TODO: Need to configure the schedule links to navigate and select the correct schedule when selected
												After the Interactive Scheduler page is built. -->
							<td>
								<a href="#" @click="routeToSchedule(schedule.ISCHED_VIEW_CODE)">
									{{schedule.SCHOOL_TITLE}} - {{schedule.ISCHED_VIEW_DESC}}
								</a>
							</td>
							<td class="text-right">
								{{schedule.STUDENT_WINDOW_END | datetime('MMMM, DD YYYY HH:mm:ss', 'MM/DD/YYYY h:mm a')}}
							</td>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Forms Waiting Completion -->
			<template v-if="!!dataVerificationForms">
				<panel-plain id="panel-awaiting-completion"
					:title="$t('MAIN_CONTENT.StudentDataForms.sideMenuStudentDataFormsLink')"
					:show-button="false"
					panel-class="dash-student"
					icon-class="edit"
					:heading-level="2"
					v-if="showFormsBlock(dataVerificationForms) && (role === 'student' || !!selectedStudent)">
					<table class="table table-striped" v-if="showAwatingCompletion(dataVerificationForms)">
						<thead>
							<tr>
								<th>{{ $t('MAIN_CONTENT.StudentDataForms.formsAwaitingCompletionTitleCase') }}</th>
								<th class="text-right">{{ $t('MAIN_CONTENT.HomePage.windowOpenUntilLabel') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(form, idx) in dataVerificationForms.ONE_TIME_FORMS"
									:key="`data_verification_forms_${idx}`">
								<template v-if="((role === 'student' && form.STUDENT_ACCESS_FLG === 'Y') || role === 'parent' && form.PARENT_ACCESS_FLG === 'Y')">
									<td>
										<a href="#" @click.prevent="goToForm(form)">
											{{form.DV_FORM_NAME}}
										</a> <i v-if="form.FORM_STATUS_FLG === 'O'">
											<small>{{ $t( 'MAIN_CONTENT.HomePage.formStarted' ) }}</small>
										</i>
										<p v-if="form.DV_FORM_NAME !== form.DV_FORM_DESC" class="text-muted">{{form.DV_FORM_DESC}}</p>
									</td>
									<td class="text-right">{{form.DV_FORM_END_DT | datetime('MMMM, DD YYYY HH:mm:ss', 'MM/DD/YYYY h:mm a')}}</td>
								</template>
							</tr>
						</tbody>
					</table>

					<table class="table table-striped" v-if="dataVerificationForms.PENDINGFORMS.length>0">
						<thead>
							<tr>
								<th>{{ $t('MAIN_CONTENT.StudentDataForms.pendingFormLabel') }}</th>
								<th class="text-right">{{ $t('MAIN_CONTENT.StudentDataForms.submittedTitleCase') }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(form, idx) in dataVerificationForms.PENDINGFORMS"
									:key="`data_verification_forms_${idx}`">
								<template v-if="((role === 'student' && form.STUDENT_ACCESS_FLG === 'Y') || role === 'parent' && form.PARENT_ACCESS_FLG === 'Y')">
									<td>
										<a href="#" @click.prevent="goToForm(form)">
											{{form.DV_FORM_NAME}}
										</a>
										<p v-if="form.DV_FORM_NAME !== form.DV_FORM_DESC" class="text-muted">{{form.DV_FORM_DESC}}</p>
									</td>
									<td class="text-right">{{form.SUBMITTED_DATE | datetime('MMMM, DD YYYY HH:mm:ss', 'MM/DD/YYYY h:mm a')}}</td>
								</template>
							</tr>
						</tbody>
					</table>
					<table class="table table-striped" v-if="dataVerificationForms.RECENTLY_COMPLETED_FORMS.length>0">
						<thead>
							<tr>
								<th>{{ $t( 'MAIN_CONTENT.StudentDataForms.recentFormLabel' ) }}</th>
								<th class="text-right">{{ $t( 'MAIN_CONTENT.StudentDataForms.status' ) }}</th>
							</tr>
						</thead>
						<tbody>
							<tr v-for="(form, idx) in dataVerificationForms.RECENTLY_COMPLETED_FORMS"
									:key="`data_verification_forms_${idx}`">
								<template v-if="((role === 'student' && form.STUDENT_ACCESS_FLG === 'Y') || role === 'parent' && form.PARENT_ACCESS_FLG === 'Y')">
									<td>
										<a href="#" @click.prevent="goToForm(form)">
											{{form.DV_FORM_NAME}}
										</a>
										<p v-if="form.DV_FORM_NAME !== form.DV_FORM_DESC" class="text-muted">{{form.DV_FORM_DESC}}</p>
									</td>
									<td class="text-right">{{form.FORM_STATUS_FLG == "A" ? $t( 'MAIN_CONTENT.StudentDataForms.approvedTitleCase' ):$t( 'MAIN_CONTENT.StudentDataForms.declinedTitleCase' )}}</td>
								</template>
							</tr>
						</tbody>
					</table>
				</panel-plain>
			</template>

			<!-- Recent School Posting -->
			<panel-plain id="panel-school-postings"
									:title="$t('MAIN_CONTENT.HomePage.recentSchoolPostingsLabel')"
									:show-button="false"
									panel-class="dash-student"
									icon-class="edit"
									:heading-level="2"
									v-if="roleRules.REPORTS_FLG === 'Y' && schoolPostings.length > 0 && (role === 'student' || !!selectedStudent)">
				<table class="table table-striped">
					<thead>
						<tr>
							<th>{{ $t('MAIN_CONTENT.HomePage.itemLabel') }}</th>
							<th class="text-right">{{ $t('MAIN_CONTENT.HomePage.postedLabel') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(post, index) in schoolPostings" :key="`recent_school_posting_${index}`">
							<td>
								<a :href="post.REPORT_URL" target="_blank" class="pointer">
									{{ post.REPORT_TITLE }}
								</a>
							</td>
							<td class="text-right">{{ post.MODIFIED_DATE }}</td>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Recent Attendnace -->
			<panel-plain id="panel-attendance"
									:title="$t('MAIN_CONTENT.HomePage.recentAttendanceLabel')"
									:titleAsLink="true"
									:show-button="false"
									panel-class="dash-student"
									icon-class="check"
									:heading-level="2"
									:show-footer="false"
									:panelLinkAddress="'/attendance/'"
									v-if="roleRules.ATTENDANCE_FLG === 'Y' && (role === 'student' || !!selectedStudent)">
				<p>
					<em>
						<template v-if="attendance.length">
							{{ $t('MAIN_CONTENT.HomePage.showingAttendanceFromPastSevenDaysLabel') }}
						</template>
						<template v-else>
							{{ $t('MAIN_CONTENT.HomePage.noAttendanceFromPastSevenDaysLabel') }}
						</template> 
						<a href="#" @click.prevent="seeAllAttendance()"><strong>{{ $t('MAIN_CONTENT.HomePage.seeAllLabel') }}</strong></a>
					</em>
				</p>

				<table class="table table-striped" v-if="attendance.length">
					<thead>
						<tr>
							<th>{{ $t('MAIN_CONTENT.HomePage.dateLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.dayLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.attendanceDescriptionLabel') }}</th>
							<th v-if="roleRules.ATTENDANCE_MINUTES_FLG === 'Y'">{{ $t('MAIN_CONTENT.HomePage.tardyMinutesLabel') }}</th>
							<th v-if="roleRules.ATTENDANCE_COMMENTS_FLG === 'Y'">{{ $t('MAIN_CONTENT.HomePage.commentsLabel') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="attendance in attendance" :key="attendance.DATE">
							<td>{{attendance.DATE | date('MMMM, DD YYYY HH:mm:ss')}}</td>
							<td>{{attendance.DATE | day('MMMM, DD YYYY HH:mm:ss')}}</td>
							<td>{{attendance.ATTENDANCE_CODE}} - {{attendance.ATTENDANCE_CODE_DESC}}</td>
							<td v-if="roleRules.ATTENDANCE_MINUTES_FLG === 'Y'">{{attendance.TARDY_MINUTES}}</td>
							<td v-if="roleRules.ATTENDANCE_COMMENTS_FLG === 'Y'">{{attendance.COMMENTS}}</td>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Recent Grade Updates -->
			<panel-plain id="panel-grade-updates"
									:title="$t('MAIN_CONTENT.HomePage.recentGradeUpdatesLabel')"
									:titleAsLink="true"
									:show-button="false"
									panel-class="dash-student"
									icon-class="book"
									:heading-level="2"
									:show-footer="false"
									:panelLinkAddress="'/courses-grades/'"
									v-if="roleRules.GRADES_FLG === 'Y' && (role === 'student' || !!selectedStudent)">
				<p>
					<em>
						<template v-if="gradeUpdates.length">{{ $t('MAIN_CONTENT.HomePage.showingGradeUpdatesFromThePastSevenDays') }}</template>
						<template v-else>{{ $t('MAIN_CONTENT.HomePage.noGradeUpdatesFromThePastSevenDays') }}</template> 
					</em>
				</p>

				<table class="table table-striped" v-if="gradeUpdates.length">
					<thead>
						<tr>
							<th>{{ $t('MAIN_CONTENT.HomePage.gradeMarkingPeriodLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.classLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.updatedLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.teacherLabel') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(grade, index) in gradeUpdates" :key="`recent_grade_updates_${index}`">
							<template v-if="!!grade.PWP_RUNNING_GRADE_CODE && !!grade.COURSE_SECTION_GUID">
								<td>
									<router-link :to="{ 
										name: 'ClassPageGradesBreakdown', 
										params:{ 
											courseSectionGUID: grade.COURSE_SECTION_GUID
										}, 
										query: { 
											studentRid: grade.STUDENT_RID, 
											mpCode: grade.MP_CODE 
										} 
									}">
										{{grade.GRADE_TO_SHOW}} ({{grade.MP_CODE}})
									</router-link>
								</td>
								<td>
									<router-link :to="{ name: 'ClassPageHome', params:{ courseSectionGUID: grade.COURSE_SECTION_GUID} }">
										{{grade.COURSE_SECTION_TITLE}}
									</router-link>
								</td>
								<td>{{grade.LAST_CALCULATED_DATE | datetime('MMMM, DD YYYY HH:mm:ss', 'MM/DD/YYYY h:mm a')}}</td>
								<td>{{grade.TEACHER_NAME}}</td>
							</template>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Recent Class Postings -->
			<panel-plain id="panel-class-postings"
									:title="$t('MAIN_CONTENT.HomePage.recentClassPostingsLabel')"
									:show-button="false"
									panel-class="dash-student"
									icon-class="calendar-alt"
									:heading-level="2"
									v-if="roleRules.GRADES_FLG === 'Y' && classPostings.length > 0 && (role === 'student' || !!selectedStudent)"
									:titleAsLink="true"
									:panelLinkAddress="'/courses-grades'">
				<table class="table table-striped">
					<thead>
						<tr>
							<th>{{ $t('MAIN_CONTENT.HomePage.itemLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.classLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.postedLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.teacherLabel') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(posting, index) in classPostings" :key="`recent_class_postings_${index}`">
							<template v-if="getPostingIcon(posting).length > 0">
								<td @clicked="posting.CLICKED = !posting.CLICKED" style="max-width: 700px;">
									<font-awesome-icon :icon="getPostingIcon(posting)" v-if="getPostingIcon(posting) !== 'folder'"></font-awesome-icon>
									<font-awesome-icon icon="folder" v-if="getPostingIcon(posting) === 'folder' && posting.CLICKED"></font-awesome-icon>
									<font-awesome-icon icon="folder-open" v-if="getPostingIcon(posting) === 'folder' && !posting.CLICKED"></font-awesome-icon>

									<!-- Checks if it is a file -->
									<template v-if="posting.REPORT_TYPE === 'F'">
										<a @click="logClassPageActivity(posting)" :href="posting.REPORT_URL" target="_blank">{{ posting.REPORT_NAME }}</a>
									</template>

									<!-- Checks if it is a forum -->
									<template v-if="!!posting.FORUM_ID">
										<a href="#" @click.prevent="goToForum(posting)">
											{{ posting.REPORT_NAME }} 
											<template v-if="posting.NEW_POST_FLG === 'Y'">
												<span style="color: red;">*New Posts!</span>
											</template>
										</a>
									</template>

									<!-- Checks if it is a dropbox -->
									<template v-if="posting.REPORT_TYPE === 'D'">
										<a href="#" @click.prevent="goToAssignmentBin(posting)">
											{{ posting.REPORT_NAME }}
										</a>
									</template>

									<!-- Checks if it is a Page -->
									<template v-if="posting.REPORT_TYPE === 'P'">
										<a href="#" @click.prevent="goToSubpage(posting)">{{ posting.REPORT_NAME }}</a>
									</template>

									<!-- Checks if it is a header -->
									<template v-if="posting.REPORT_TYPE === 'H'">
										<a @click="'header'">{{ posting.REPORT_NAME }}</a>
									</template>

									<!-- Checks if it is a lesson plan -->
									<template v-if="posting.REPORT_TYPE === 'E'">
										<a @click="logClassPageActivity(posting)" :href="posting.REPORT_URL" target="_blank">{{ posting.REPORT_NAME }}</a>
									</template>

									<!-- Checks if it is a folder -->
									<template v-if="posting.REPORT_TYPE === 'O'">
										<a href="#" @click.prevent="toggleFolder(posting.LINE_NO)">{{ posting.REPORT_NAME }}</a>
										<ul v-show="isFolderOpen(posting.LINE_NO)">
											<li v-for="file in posting.FILES" :key="file.REPORT_ID">
												<a target="_blank" :href="file.REPORT_URL">{{file.CONTENT_FILENAME}}</a>
											</li>
										</ul>
									</template>

									<!-- Checks if it is a glossary -->
									<template v-if="posting.REPORT_TYPE === 'G'">
										<a href="#" @click.prevent="goToGlossary(posting)">
											{{ posting.REPORT_NAME }}
										</a>
									</template>

									<!-- Check if this is a link -->
									<template v-if="posting.REPORT_TYPE === 'L'">
										<a @click="logClassPageActivity(posting)" :href="posting.LINK_LOCATION" target="_blank">{{ posting.REPORT_NAME }}</a>
									</template>

									<!-- Check if this isn't a specific type and needs to be plain text -->
									<template v-if="posting.REPORT_TYPE === '' || posting.REPORT_TYPE === 'A'">
										{{ posting.REPORT_NAME }}
									</template>

									<!-- Description field -->
									<template v-if="posting.REPORT_DESCRIPTION.length > 0 && !(posting.REPORT_TYPE === 'P' || posting.REPORT_TYPE === 'H')">
										<br />
										<span style="font-size: 11px;" v-html="getString(posting.REPORT_DESCRIPTION)">
										</span>
									</template>
								</td>

								<td>
									<router-link :to="{ name: 'ClassPageHome', params: { courseSectionGUID: posting.COURSE_SECTION_GUID } }">
										{{ posting.COURSE_SECTION_TITLE }}
									</router-link>
								</td>
								<td>
									{{ getPostDate(posting) | datetime('MMMM, DD YYYY HH:mm:ss', 'ddd - MM/DD/YY') }}
								</td>
								<td>
									{{ posting.TEACHER_NAME }}
								</td>
							</template>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Recent Discipline -->
			<panel-plain id="panel-discipline"
									:title="$t('MAIN_CONTENT.HomePage.recentDisciplineLabel')"
									:titleAsLink="true"
									:show-button="false"
									panel-class="dash-student"
									icon-class="gavel"
									:heading-level="2"
									:show-footer="false"
									:panelLinkAddress="'/discipline/'"
									v-if="roleRules.DISCIPLINE_FLG === 'Y' && (role === 'student' || selectedStudent)">

				<p>
					<em>
						<template v-if="Object.keys(discipline).length">
							{{ $t('MAIN_CONTENT.HomePage.showingDisciplineFromThePastSevenDays') }}
						</template>
						<template v-else>
							{{ $t('MAIN_CONTENT.HomePage.noDisciplineFromThePastSevenDays') }}
						</template> 
						<router-link to="/discipline">
							<strong>{{ $t('MAIN_CONTENT.HomePage.seeAllLabel') }}</strong>
						</router-link>
					</em>
				</p>
				<table class="table table-striped" v-if="Object.keys(discipline).length">
					<thead>
						<tr>
							<th>{{ $t('MAIN_CONTENT.HomePage.dateLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.commonContent.schoolLabel') }}</th>
							<th v-if="roleRules.DISCIPLINE_INFRACTION_FLG === 'Y'">{{ $t('MAIN_CONTENT.HomePage.infractionsLabel') }}</th>
							<th v-if="roleRules.DISCIPLINE_ACTION_FLG === 'Y'">{{ $t('MAIN_CONTENT.HomePage.actionsTakenLabel') }}</th>
						</tr>
					</thead>
					<tbody v-for="(incidentsForSchool, schoolId) in discipline" :key="schoolId">
						<tr v-for="incident in incidentsForSchool" :key="incident.INCIDENT_RID">
							<td>
									{{ incident.INCIDENT_DATE }} {{ incident.INCIDENT_TIME }}
							</td>
							<td> {{incident.SCHOOL_TITLE}} </td>
							<td v-if="roleRules.DISCIPLINE_INFRACTION_FLG === 'Y'">
								
								<ul v-if="Object.keys(incident.INFRACTIONS).length > 1" style="margin-left: -25px;">
									<li v-for="(infraction) in incident.INFRACTIONS" :key="infraction">
										{{ infraction }}
									</li>
								</ul>
								<template v-else-if="Object.keys(incident.INFRACTIONS).length === 1">{{incident.INFRACTIONS[1]}}</template>
							</td>
							<td v-if="roleRules.DISCIPLINE_ACTION_FLG === 'Y'">
								<ul v-if="Object.keys(incident.ACTIONS).length > 1" style="margin-left: -25px;">
									<li v-for="(action) in incident.ACTIONS" :key="action">
										{{ action }}
									</li>
								</ul>
								<template v-else-if="Object.keys(incident.ACTIONS).length === 1">{{incident.ACTIONS[1]}}</template>
							</td>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Recent Fees -->
			<panel-plain id="panel-fees"
									:title="$t('MAIN_CONTENT.HomePage.recentFeesLabel')"
									:titleAsLink="true"
									:show-button="false"
									panel-class="dash-student"
									icon-class="piggy-bank"
									:heading-level="2"
									:show-footer="false"
									:panelLinkAddress="'/fees/'"
									v-if="roleRules.FEES_FLG === 'Y' && (role === 'student' || !!selectedStudent)">

				<h4 v-if="district.CWP_ALLOW_PAYMENTS_FLG == 'N'">
					{{ $t('MAIN_CONTENT.HomePage.balanceOwedToSchoolLabelNoProp') }} <strong :class="{'text-danger': feesBalance > 0}">{{feesBalance | dollar}}</strong>
				</h4>

				<payment-portal v-if="district.CWP_ALLOW_PAYMENTS_FLG == 'Y'" :balance="externalFeesBalance"/>
				<p v-if="district.CWP_ALLOW_PAYMENTS_FLG == 'N'">
					<em>
						<template v-if="fees.length">
							{{ $t('MAIN_CONTENT.HomePage.showingTransactionsFromThePastSevenDaysLabel') }}
						</template>
						<template v-else>
							{{ $t('MAIN_CONTENT.HomePage.noTransactionsFromThePastSevenDaysLabel') }}
						</template> 
						<router-link to="/fees">
							<strong>{{ $t('MAIN_CONTENT.HomePage.seeAllLabel') }}</strong>
						</router-link>
					</em>
				</p>
				<table class="table table-striped" v-if="fees.length && district.CWP_ALLOW_PAYMENTS_FLG == 'N'">
					<thead>
						<tr>
							<th>{{ $t('MAIN_CONTENT.HomePage.dateLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.typeLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.feesLabel') }}</th>
							<th>{{ $t('MAIN_CONTENT.HomePage.paymentsLabel') }}</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="fee in recentFees" :key="fee.TRANS_ID">
							<td>{{fee.TRANS_DATE | date('MMMM, DD YYYY', 'MM/DD/YYYY')}}</td>
							<td>{{fee.STUD_FEE_CAT_DESC}}</td>
							<td><template v-if="fee.TRANS_TYPE === 'FEE' || fee.TRANS_TYPE === 'REFUND'">{{ (fee.TRANS_AMOUNT) | dollar }}</template></td>
							<td><template v-if="fee.TRANS_TYPE === 'PAYMENT' || fee.TRANS_TYPE === 'DEPOSIT'">{{ (fee.TRANS_AMOUNT*-1) | dollar }}</template></td>
						</tr>
					</tbody>
				</table>
			</panel-plain>

			<!-- Lunch Menu -->
			<panel-plain id="panel-lunch-menu"
									:title="$t('MAIN_CONTENT.HomePage.lunchMenuLabel')"
									:show-button="false"
									panel-class="dash-student"
									icon-class="utensils"
									:heading-level="2"
									:titleAsLink="true"
									:panelLinkAddress="'/lunch-menus'"
									v-if="lunchMenus && lunchMenus.length">
				<div class="row" v-if="typeof lunchMenus !== 'undefined' && lunchMenus !== null && lunchMenus.length >0">
					<div class="col-xs-12"
							v-for="(school, schoolIndex) in allStudentSchools"
							:key="`lunchMenu_school_${schoolIndex}`">
						<div class="row">
							<div class="col-xs-12">
								<h4>{{ school.SCHOOL_TITLE }}</h4>
							</div>
							<template v-for="(menu, index) in lunchMenus">
								<div class="week-item col-xs-2"
										v-if="menu.SCHOOL_ID === school.SCHOOL_ID"
										:key="index">
									<div class="panel panel-neutral" >
										<div class="panel-heading">
											<span> {{ menu.LUNCH_DATE | datetime('MMMM, DD YYYY HH:mm:ss', 'dddd - MM/DD/YYYY') }} </span>
										</div>
										<div class="panel-body" style="white-space: pre-line">
											{{ menu.LUNCH_MENU }}
										</div>
									</div>
								</div>
							</template>
							<div class="col-xs-12" v-if="!schoolsWithMenus.includes(school.SCHOOL_ID)">
								{{ $t('MAIN_CONTENT.HomePage.nothingForThisWeek') }}
							</div>
						</div>
					</div>
				</div>
			</panel-plain>
		</template>
	</div>
</template>

<script>
	import moment from 'moment';
	import { VTooltip, VPopover, VClosePopover } from 'v-tooltip';
	import { mapState, mapGetters } from 'vuex';

	import PanelPlain from 'COMMONS/panels/PanelPlain.vue';
	import PanelBasic from 'COMMONS/panels/PanelBasic.vue';
	import Modal from 'COMMONS/shared/Modal/Modal.vue';

	import Announcements from './Announcements.vue';
	import ParentHomePage from './ParentHomePage.vue';
	import AssignmentsCarousel from "./classPage/AssignmentsOwlCarousel.vue";
	import AssignmentsGrid from "./classPage/AssignmentsGrid.vue";
	import AssignmentsList from "./classPage/AssignmentsList.vue";

	import Recent from '../api/Recent';
	import Lunches from '../api/Lunches';
	import AnnouncementsList from '../api/Announcements';
	import LogClassPageActivityResource from '../api/ClassPageLog';
	import CovidSurvey from '../api/CovidSurvey';
	import Profile from '../api/Profile';
	import Locales from '../api/Locales';
	import DistrictImage from '../api/DistrictImage';
	import PaymentPortal from './PaymentPortal.vue';

	export default {
		name: 'HomePage',
		data() {
			return {
				locales: [],
				selectedLocale: '',
				gradeUpdates: [],
				schoolPostings: [],
				dataVerificationForms : [],
				interactiveSchedules: [],
				announcements: [],
				assignments: [],
				lunchMenus: [],
				courseRequestForms: [],
				classPostings: [],
				graduationProgress: {},
				fees: [],
				feesBalance: '',
				externalFeesBalance: {},
				district: {},
				covidLink: '',
				assignmentsView: 'slider',
				selectLocale: false,
				showLocaleSaveError: false,
				attendance: [],
				discipline: {},
				openFolders: [],
				owlKey: 0
			}
		},
		computed: {
			...mapGetters([
				'selectedStudent',
				'roleRules',
				'allStudentSchools',
				'role',
				'locale',
				'userID',
				'userIsAdmin',
			]),
			percentCreditsEarned() {
				let percent = !!this.graduationProgress.TRACK_RULE_TOTALS ? Math.round((this.graduationProgress.TRACK_RULE_TOTALS.REQUIRED_CREDITS_EARNED / this.graduationProgress.TRACK_RULE_TOTALS.TOTAL_CREDITS) * 100) : 0;

				if(percent > 100) {
					percent = 100;
				}

				return `${percent}%`;
			},
			percentCreditsInProgress() {
				let percent = !!this.graduationProgress.TRACK_RULE_TOTALS ?
					Math.round((this.graduationProgress.TRACK_RULE_TOTALS.REQUIRED_CREDITS_IN_PROGRESS / this.graduationProgress.TRACK_RULE_TOTALS.TOTAL_CREDITS) * 100) :
					0;

				if(!!this.graduationProgress.TRACK_RULE_TOTALS) {
					if(Math.round((this.graduationProgress.TRACK_RULE_TOTALS.REQUIRED_CREDITS_IN_PROGRESS / this.graduationProgress.TRACK_RULE_TOTALS.TOTAL_CREDITS) * 100) + percent > 100) {
						percent = 100 - Math.round((this.graduationProgress.TRACK_RULE_TOTALS.REQUIRED_CREDITS_IN_PROGRESS / this.graduationProgress.TRACK_RULE_TOTALS.TOTAL_CREDITS) * 100);
					}
				}
				return `${percent}%`;
			},
			schoolsWithMenus() {
				let menus = [];

				for(let l in this.lunchMenus) {
					let menu = this.lunchMenus[l];

					if(!menus.includes(menu.SCHOOL_ID)) {
						menus.push(menu.SCHOOL_ID);
					}
				}

				return menus;
			},
			getDisplayStyle() {
				return this.assignments.length > 3 ? 'displayBlock' : 'displayGrid';
			},
			dueSoon() {
				return this.assignments.filter( a => moment( a.END_DATE ).isSameOrBefore( moment().add( 2, 'day' ) ) );
			},
			recentFees(){
				return this.fees.reverse();
			},
		},
		watch: {
			selectedStudent: {
				immediate: true,
				handler: function(newValue, oldValue) {
					if(!!newValue) {
						this.loadHomePageData();
					}
				},
			},
		},
		components: {
			AssignmentsCarousel,
			AssignmentsGrid,
			AssignmentsList,
			PanelPlain,
			PanelBasic,
			Announcements,
			ParentHomePage,
			Modal,
			PaymentPortal,
		},
		directives: {
			'tooltip': VTooltip,
		},
		methods: {
			getPostDate(post){
				if( moment(post.START_DATE).isAfter( moment(post.CREATED_DATE) ) ){
					return post.START_DATE;
				} 
				
				return post.CREATED_DATE;
				
			},
			toggleFolder(id) {
				if( this.openFolders.includes(id) ) {
					const index = this.openFolders.indexOf(id);
					if (index > -1) {
						this.openFolders.splice(index, 1);
					}
				} else {
					this.openFolders.push(id);
				}
			},
			isFolderOpen(id) {
				return this.openFolders.includes(id);
			},
			logClassPageActivity(item) {
				const logClassPageActivity = new LogClassPageActivityResource({
					routeParams: {
						courseSectionGuid: item.COURSE_SECTION_GUID,
					},
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					},
					data: {
						"folder_type": item.FOLDER_TYPE,
						"line_no": item.LINE_NO
					},
				});

				logClassPageActivity.post();
			},
			goToForm(form) {
				this.$router.push({
					name: form.FORM_TYPE == 'S' ? 'OneTimeForm' : 'OnDemandForm',
					params: {
						formID: form.DV_FORM_ID,
						instanceID: form.INSTANCE_ID,
						formStatus: form.FORM_STATUS_FLG
					},
					query: {
						referrer: 'home',
						formStatus: form.FORM_STATUS_FLG
					},
				});
			},
			goToSubpage(item) {
				this.logClassPageActivity(item);
				
				const viewsMap = {
					"C": "ClassPageSubpage",
					"G": "ClassPageGroupSubpage",
					"S": "ClassPageStudentSubpage",
					"P": "ClassPageSectionSubpage"
				};
				const view = viewsMap[item.FOLDER_TYPE];

				if( item.FOLDER_TYPE === 'G' ) {
					params.groupId = item.FOLDER_ID;
				}

				if( item.FOLDER_TYPE === 'S') {
					params.studentRid = this.selectedStudent.STUDENT_RID;
				}

				this.$router.push({
					name: view, 
					params: {
						courseSectionGUID: item.COURSE_SECTION_GUID,
						studentRid: this.selectedStudent.STUDENT_RID,
						lineNo: item.LINE_NO
					}
				});
			},
			goToAssignmentBin(item) {
				this.logClassPageActivity(item);

				const viewsMap = {
					"C": "ClassPageAssignmentBin",
					"G": "ClassPageGroupAssignmentBin",
					"S": "ClassPageStudentAssignmentBin",
					"P": "ClassPageSectionAssignmentBin"
				};

				const view = viewsMap[item.FOLDER_TYPE];

				let params = {
					courseSectionGUID: item.COURSE_SECTION_GUID,
					studentRid: this.selectedStudent.STUDENT_RID,
					assignmentBinId: item.LINE_NO
				};

				if( item.FOLDER_TYPE === 'G' ) {
					params.groupId = item.FOLDER_ID;
				}

				if( item.FOLDER_TYPE === 'S') {
					params.studentRid = this.selectedStudent.STUDENT_RID;
				}

				this.$router.push({
					name: view, 
					params: params,
				});
			},
			goToForum(item) {
				this.logClassPageActivity(item);

				const viewsMap = {
					"C": "ClassPageForum",
					"G": "ClassPageGroupForum",
					"S": "ClassPageStudentForum",
					"P": "ClassPageSectionForum"
				};
				const view = viewsMap[item.FOLDER_TYPE];

				let params = {
					courseSectionGUID: item.COURSE_SECTION_GUID,
					studentRid: this.selectedStudent.STUDENT_RID,
					forumId: item.FORUM_ID
				};

				if( this.folderType === 'G' ) {
					params.groupId = item.FOLDER_ID;
				}

				if( this.folderType === 'S') {
					params.studentRid = this.selectedStudent.STUDENT_RID;
				}

				this.$router.push({
					name: view, 
					params: params,
				});
			},
			goToGlossary(item) {
				this.logClassPageActivity(item);

				this.$router.push({
					name: 'CoursesGradesGlossary',
					params: {
						courseSectionGUID: item.COURSE_SECTION_GUID,
						glossaryType: item.FOLDER_TYPE,
						glossaryID: item.LINE_NO
					}
				});
			},
			getDistrictImage() {
				const self = this;
				const district = new DistrictImage({});
				const promise = district.get();
				promise.then(response => {
					self.$set(self, 'district', district.getResponseData());
				});
			},
			loadHomePageData() {
				const self = this;
				let recents = new Recent({
					query: {
						student_rid: this.selectedStudent.STUDENT_RID,
					}
				});
				const p = recents.get();

				p.then(response => {
					let data = response.data.data;

					self.$set(self, 'interactiveSchedules', data.INTERACTIVE_SCHEDULES);
					self.$set(self, 'announcements', data.ANNOUNCEMENTS);
					self.$set(self, 'schoolPostings', data.SCHOOL_POSTINGS);
					self.$set(self, 'dataVerificationForms', data.DATA_VERIFICATION_FORMS);
					self.$set(self, 'courseRequestForms', data.COURSE_REQUEST_FORMS);
					self.$set(self, 'assignments', data.ASSIGNMENTS);
					self.$set(self, 'classPostings', data.CLASS_POSTINGS);
					self.$set(self, 'graduationProgress', data.GRADUATION_PROGRESS);
					self.$set(self, 'lunchMenus', data.LUNCHES);
					self.$set(self, 'gradeUpdates', data.GRADE_UPDATES);
					self.$set(self, 'fees', data.FEES);
					self.$set(self, 'feesBalance', data.FEES_BALANCE);
					self.$set(self, 'externalFeesBalance', data.EXTERNAL_FEE_BALANCE);
					self.$set(self, 'attendance', data.ATTENDANCE);
					self.$set(self, 'discipline', data.DISCIPLINE);

					self.owlKey++;

					for(let i in self.classPostings) {
						let post = self.classPostings[i];

						self.$set(post, 'CLICKED', false);
					}

					self.loadCovidLink();
				})
				.catch(error => {
					console.log(error);
				});
			},
			loadLunchMenus() {
				const self = this;
				let schools = [];

				let start = moment().isoWeekday( 1 ).format( 'MM/DD/YYYY' );
				let end = moment().isoWeekday( 5 ).format( 'MM/DD/YYYY' );

				for( let s in self.allStudentSchools ) {
					let school = self.allStudentSchools[ s ];

					schools.push( school.SCHOOL_ID );
				}

				const lunches = new Lunches( {
					query: {
						school_id: schools.join( ',' ),
						start_date:	start,
						end_date:		end
					}
				} );

				const promise = lunches.get();

				promise.then(response => {
					self.$set(self, 'lunchMenus', lunches.getResponseData());
					self.loadAnnouncements();
				});
			},
			loadAnnouncements() {
				const self = this;
				let schools = [];

				for(let s in self.allStudentSchools) {
					let school = self.allStudentSchools[s];

					schools.push(school.SCHOOL_ID);
				}

				const announcements = new AnnouncementsList({
					query: {
						school_id: schools.join(',')
					}
				});

				const promise = announcements.get();

				promise.then(response => {
					self.$set(self, 'announcements', announcements.getResponseData());
				});
			},
			loadCovidLink() {
				const self = this;

				const survey = new CovidSurvey({
					query: {
						studentRid: self.selectedStudent.STUDENT_RID,
						answeredByType: self.role === 'parent' ? 'C' : 'S'
					}
				});

				survey.get().then(response => {
					self.$set(self, 'covidLink', survey.getResponseData().LINK);
				});
			},
			showFormsBlock(forms) {
				if(this.showAwatingCompletion(forms)){
					return true;
				}

				if(this.showPendingForms(forms)){
					return true;
				}

				if(this.showCompletedForms(forms)){
					return true;
				}
				
				
				return false;
			},
			showCompletedForms(forms){
				let show = false;
				if(!!forms.RECENTLY_COMPLETED_FORMS){
					for(let i in forms.RECENTLY_COMPLETED_FORMS) {
						let form =  forms.RECENTLY_COMPLETED_FORMS[i];
						if(form.FORM_STATUS_FLG != "P"){
							if(this.role === 'student' && form.STUDENT_ACCESS_FLG === 'Y') {
							show = true;
							} else if(this.role === 'parent' && form.PARENT_ACCESS_FLG === 'Y') {
								show = true;
							}
						}
					}
				}
				return show;
			},
			showPendingForms(forms){
				let show = false;
				if(!!forms.PENDINGFORMS){
					for(let i in forms.PENDINGFORMS) {
						let form =  forms.PENDINGFORMS[i];
						if(form.FORM_STATUS_FLG == "P"){
							if(this.role === 'student' && form.STUDENT_ACCESS_FLG === 'Y') {
							show = true;
							} else if(this.role === 'parent' && form.PARENT_ACCESS_FLG === 'Y') {
								show = true;
							}
						}
					}
				}
				return show;
			},
			showAwatingCompletion(forms) {
				let show = false;
				if(!!forms.ONE_TIME_FORMS){
					for(let i in forms.ONE_TIME_FORMS) {
						let form =  forms.ONE_TIME_FORMS[i];

						if(this.role === 'student' && form.STUDENT_ACCESS_FLG === 'Y') {
							show = true;
						} else if(this.role === 'parent' && form.PARENT_ACCESS_FLG === 'Y') {
							show = true;
						}
					}
				}
				
				return show;
			},
			getPostingIcon(post) {
				let icon = '';
				let postValues = this.getPostingValues(post);

				if(postValues.hasfile) {
					icon = 'paperclip';
				} else if(postValues.haslink) {
					icon = 'link';
				} else if(postValues.hasforum) {
					icon = 'comment';
				} else if(postValues.hasFolder) {
					icon = 'folder';
				} else if(postValues.hasDropBox) {
					icon = 'file';
				} else if(postValues.hasPage) {
					icon = 'globe-americas';
				} else if(postValues.hasHeader) {
					// do nothing
				} else if(postValues.hasGlossary) {
					icon = 'book';
				} else if(postValues.hasLessonPlan) {
					icon = 'clipboard-list';
				} else {
					icon = 'exclamation-triangle';
				}

				return icon;
			},
			getPostingValues(post) {
				let pObject = {
					thisonclick:		'',
					haslink:				false,
					hasfile:				false,
					hasforum:				false,
					announcement:		true,
					hasDesc:				false,
					hasDropBox:			false,
					hasPage:				false,
					hasHeader:			false,
					hasLessonPlan:	false,
					hasFolder:			false,
					hasGlossary:		false
				};

				if(post.REPORT_ID.length > 0) {
					// TODO: Update this with the correct link
					pObject.thisonclick = Request.Approot&"/ReportViewer.cfm?PRODUCT_ID=GBK&REPORT_ID="&post.REPORT_ID;
					pObject.hasfile = true;
					pObject.announcement = false;
				} else if(post.LINK_LOCATION.length > 0) {
					pObject.thisonclick = post.LINK_LOCATION;
					pObject.haslink = true;
					pObject.announcement = false;
				} else if(post.FORUM_ID.length) {
					pObject.hasforum = true;
					pObject.announcement = false;
				} else if(post.REPORT_TYPE === 'D') {
					pObject.hasDropBox = true;
					pObject.announcement = false;
				} else if(post.REPORT_TYPE === 'P') {
					pObject.hasPage = true;
					pObject.announcement = false;
				} else if(post.REPORT_TYPE === 'H') {
					pObject.hasHeader = true;
					pObject.announcement = false;
				} else if(post.REPORT_TYPE === 'E') {
					pObject.hasLessonPlan = true;
					pObject.announcement = false;
				} else if(post.REPORT_TYPE === 'O') {
					pObject.hasFolder = true;
					pObject.announcement = false;
				} else if(post.REPORT_TYPE === 'G') {
					pObject.hasGlossary = true;
					pObject.announcement = false;
				}

				if(post.REPORT_DESCRIPTION.length > 0) {
					pObject.hasDesc = true;
				}

				pObject.NewPost = false;

				return pObject;
			},
			getString(str) {
				return str.replace(/&quot;/g, '"');
			},
			routeToSchedule(scheduleCode) {
				this.$router.push({
					name: 'InteractiveScheduler',
					query: {
						scheduleID: scheduleCode
					}
				});
			},
			seeAllAttendance() {
				this.$router.push({
					name: 'Attendance'
				});
			},
			routeToCourseRequestForms() {
				this.$router.push({
					name: 'courseRequestForms'
				});
			},
			fetchLocales() {
				const self = this;

				let locales = new Locales({});

				locales.get().then(response => {
					self.locales = locales.getResponseData();
					self.selectLocale = true;
				})
				.catch(error => {
					self.locales = [];
				});
			},
			saveLocale() {
				const self = this;
				const selectedLocaleOption = this.locales.find( locale => this.selectedLocale === locale.LOCALE_ID );

				let profile = new Profile({
					routeParams: { setting: 'locale' },
					data: {
						'locale': selectedLocaleOption.LOCALE_ID,
						'user_id': this.userID
					}
				});

				profile.put().then(response => {
					if( profile.getResponseData().TYPE === 'success' ) {
						self.$store.state.profile.LOCALE = JSON.parse( JSON.stringify( selectedLocaleOption ) );
						self.$i18n.locale = selectedLocaleOption.LOCALE_CODE;
						self.closeModal();
					} else {
						self.showLocaleSaveError = true;
					}
				})
			},
			closeModal() {
				this.selectLocale = false;
				this.showLocaleSaveError = false;
			},
		},
		created() {
			this.$store.commit('setUseHeader', false);
		
			this.getDistrictImage();

			if( !this.selectedStudent ) {
				this.loadLunchMenus();
			}

			if( !this.userIsAdmin ) {
				if( !Object.keys( this.locale ).length > 0 ) {
					this.fetchLocales();
				} else {
					if( !(this.locale.LOCALE_CODE.length > 0) ) {
						this.fetchLocales();
					}
				}
			}
		}
	}
</script>
